import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ComboBox, Icon, IconButton, Label, Link, PrimaryButton, Stack, Text, } from "@fluentui/react";
import { getAzureProductIconSource } from "../AzureProductIcons";
import { productComponentsSectionStyles } from "../styles/ProductComponentsSection.styles";
// Maximum number of products a user can add.
const MAX_PRODUCTS = 10;
const PRODUCT_LABEL_ID = "confidence-call-products-label";
const PRODUCT_PRIMARY_HINT_ID = "confidence-call-products-primary-hint";
const DEFAULT_PRODUCT_TILE_ICON = {
    iconName: "Product",
    iconColor: "#005a9e",
    iconBackgroundColor: "#deecf9",
};
export const getProductTileIcon = (productName) => {
    const azureProductIconSource = getAzureProductIconSource(productName.toLowerCase());
    if (azureProductIconSource) {
        return Object.assign(Object.assign({}, DEFAULT_PRODUCT_TILE_ICON), { imageSource: azureProductIconSource });
    }
    return DEFAULT_PRODUCT_TILE_ICON;
};
let productRowSeq = 0;
export const createProductRow = (value = "") => ({
    id: `product-row-${(productRowSeq += 1)}`,
    value,
});
/**
 * Builds the submitted product list from the dropdown rows. Only values that
 * are CURRENT RM2 catalogue keys are included, so every submitted `name` is an
 * exact RM2 ProductName (EM-API rejects anything else). Order follows the rows
 * and duplicates are dropped; the payload marks the first entry as primary.
 * AB#39926822.
 */
export const buildSelectedProducts = (rows, options) => {
    const optionsByKey = new Map(options.map((option) => [String(option.key), option]));
    const seen = new Set();
    const products = [];
    rows.forEach((row) => {
        var _a;
        const key = row.value.trim();
        const option = optionsByKey.get(key);
        if (!option || seen.has(key)) {
            return;
        }
        seen.add(key);
        const productId = (_a = option.data) === null || _a === void 0 ? void 0 : _a.productId;
        products.push({
            id: productId !== undefined ? String(productId) : key,
            name: key,
            source: "Picker",
        });
    });
    return products;
};
const renderProductOption = (option) => {
    if (!option) {
        return null;
    }
    const productIcon = getProductTileIcon(String(option.key));
    return (_jsxs("span", Object.assign({ style: { display: "flex", alignItems: "center", gap: 8 } }, { children: [_jsx("span", Object.assign({ "aria-hidden": "true", className: productComponentsSectionStyles.productIconContainer(productIcon.iconBackgroundColor) }, { children: productIcon.imageSource ? (_jsx("img", { alt: "", className: productComponentsSectionStyles.productImage, src: productIcon.imageSource })) : (_jsx(Icon, { className: productComponentsSectionStyles.productIcon(productIcon.iconColor), iconName: productIcon.iconName })) })), _jsx("span", { children: option.text })] })));
};
export const ProductComponentsSection = ({ isExpanded, setIsExpanded, isUnlocked, isProductComponentSectionComplete, isFormComplete, productRows, setProductRows, azureComponentOptions, azureComponentOptionsLoading, azureComponentOptionsError, onRetryAzureComponentOptions, customerTpid, customerName, customerCountry, customerCountryId, customerCountryAlpha2, customerCountryAlpha3, customerCountryIsEU, customerMsSalesId, customerSegment, isManualCustomerEntry, selectedIncidents, selectedIncidentOutageMap, selectedSupportRequestNumbers, supportTicketTitleMap, azureSubscriptionId = "", azureSubscriptionSelection, manualAzureSubscriptionId = "", subscriptionCandidates = [], subscriptionContextKey = "", selectedCallType, selectedTechnicalDepth, escalationReasons, desiredOutcome, businessImpact, stakeholders, escalationReasonSummary, moveToNextTab, }) => {
    const handleToggleExpanded = () => {
        if (!isUnlocked)
            return;
        setIsExpanded(!isExpanded);
    };
    const handleKeyDown = (e) => {
        if (!isUnlocked)
            return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
        }
    };
    const handleReviewCreateCall = () => {
        // Defense in depth: never advance while any required field is unsatisfied,
        // even if the disabled button were somehow bypassed (state desync/programmatic).
        if (!isFormComplete()) {
            return;
        }
        // Products come only from the RM2 catalogue dropdown. Outage- and
        // support-request-derived product names are not RM2 products and are never
        // submitted. AB#39926822.
        const selectedProductsArray = buildSelectedProducts(productRows, azureComponentOptions);
        const formData = {
            customerTpid: customerTpid,
            customerName: customerName,
            customerCountry: customerCountry || "",
            customerCountryId: customerCountryId || "",
            customerCountryAlpha2: customerCountryAlpha2 || "",
            customerCountryAlpha3: customerCountryAlpha3 || "",
            customerCountryIsEU: customerCountryIsEU || false,
            customerMsSalesId: customerMsSalesId || "",
            customerSegment: customerSegment || "",
            isManualCustomerEntry: !!isManualCustomerEntry,
            selectedIncidents: selectedIncidents,
            selectedIncidentOutageMap: selectedIncidentOutageMap || {},
            selectedSupportRequestNumbers: selectedSupportRequestNumbers,
            supportTicketTitleMap: supportTicketTitleMap || {},
            azureSubscriptionId,
            azureSubscriptionSelection,
            manualAzureSubscriptionId,
            subscriptionCandidates,
            subscriptionContextKey,
            type: selectedCallType,
            typeKey: selectedCallType,
            technicalDepth: selectedTechnicalDepth,
            escalationReasons,
            desiredOutcome: desiredOutcome,
            businessImpact,
            stakeholders,
            escalationReasonSummary: escalationReasonSummary || "",
            workloads: selectedProductsArray.map((product) => product.name),
            selectedProducts: selectedProductsArray,
            components: [],
        };
        if (moveToNextTab) {
            moveToNextTab(true, formData);
        }
    };
    // Values already chosen in any row. Used to gray out (disable) those options
    // in the other rows' dropdowns so the same product can't be picked twice,
    // while every option stays visible in the list. AB#39245060.
    const selectedProductValues = new Set(productRows.map((row) => row.value).filter((value) => value));
    return (_jsxs(Stack, Object.assign({ tokens: { childrenGap: 16 }, style: {
            marginTop: "24px",
            opacity: isUnlocked ? 1 : 0.6,
        } }, { children: [_jsxs("div", Object.assign({ className: isProductComponentSectionComplete() ? productComponentsSectionStyles.sectionHeaderComplete : productComponentsSectionStyles.sectionHeader, onClick: handleToggleExpanded, role: "button", tabIndex: isUnlocked ? 0 : -1, style: {
                    cursor: isUnlocked ? "pointer" : "not-allowed",
                }, onKeyDown: handleKeyDown }, { children: [_jsx(Icon, { iconName: isExpanded && isUnlocked ? "ChevronDown" : "ChevronRight", style: { fontSize: "12px" } }), isProductComponentSectionComplete() && (_jsx(Icon, { iconName: "SkypeCircleCheck", style: { fontSize: "16px", color: "#107c10" } })), _jsx(Text, Object.assign({ variant: "medium", style: { fontWeight: 600 } }, { children: "Products" })), _jsx(Text, Object.assign({ variant: "small", style: {
                            color: isProductComponentSectionComplete() ? "#107c10" : "#605e5c",
                            marginLeft: "12px",
                        } }, { children: "Select the impacted Azure products from the Escalation Manager product list." }))] })), isExpanded && isUnlocked && (_jsxs(Stack, Object.assign({ tokens: { childrenGap: 16 }, style: { marginTop: "12px" } }, { children: [_jsxs(Stack, Object.assign({ tokens: { childrenGap: 8 } }, { children: [_jsx(Label, Object.assign({ id: PRODUCT_LABEL_ID, required: true }, { children: "Select the impacted products" })), _jsx(Text, Object.assign({ id: PRODUCT_PRIMARY_HINT_ID, variant: "small", style: { color: "#605e5c" } }, { children: "The first product is submitted as the primary product." })), productRows.map((row, index) => (_jsxs(Stack, Object.assign({ horizontal: true, tokens: { childrenGap: 8 }, verticalAlign: "end" }, { children: [_jsx(ComboBox, { ariaLabel: index === 0 ? "Primary product" : `Product ${index + 1}`, ariaDescribedBy: index === 0 ? PRODUCT_PRIMARY_HINT_ID : undefined, required: index === 0, placeholder: "Select a product", options: azureComponentOptions.map((option) => selectedProductValues.has(String(option.key)) &&
                                            String(option.key) !== row.value
                                            ? Object.assign(Object.assign({}, option), { disabled: true }) : option), onRenderOption: renderProductOption, selectedKey: row.value || null, allowFreeform: false, autoComplete: "on", useComboBoxAsMenuWidth: true, disabled: !isUnlocked || azureComponentOptions.length === 0, onChange: (_event, option) => {
                                            if (!option) {
                                                return;
                                            }
                                            const selectedValue = String(option.key);
                                            setProductRows(productRows.map((r) => r.id === row.id ? Object.assign(Object.assign({}, r), { value: selectedValue }) : r));
                                        }, styles: {
                                            root: {
                                                maxWidth: "500px",
                                                flexGrow: 1,
                                            },
                                        } }), productRows.length > 1 && (_jsx(IconButton, { iconProps: { iconName: "Delete" }, ariaLabel: `Remove product ${index + 1}`, disabled: !isUnlocked, onClick: () => setProductRows(productRows.filter((r) => r.id !== row.id)) }))] }), row.id))), productRows.length < MAX_PRODUCTS && (_jsx(Link, Object.assign({ disabled: !isUnlocked, onClick: () => setProductRows([...productRows, createProductRow()]) }, { children: "+ Add another product" }))), azureComponentOptions.length === 0 && (_jsx(Stack, Object.assign({ tokens: { childrenGap: 4 } }, { children: azureComponentOptionsError ? (_jsxs(_Fragment, { children: [_jsx(Text, Object.assign({ variant: "small", style: { color: "#a4262c" } }, { children: "The product list couldn't be loaded. Please try again." })), onRetryAzureComponentOptions && (_jsx(Link, Object.assign({ disabled: !isUnlocked, onClick: onRetryAzureComponentOptions }, { children: "Retry loading products" })))] })) : azureComponentOptionsLoading ? (_jsx(Text, Object.assign({ variant: "small", style: { color: "#a4262c" } }, { children: "The product list is still loading. Please wait a moment and try again." }))) : (_jsxs(_Fragment, { children: [_jsx(Text, Object.assign({ variant: "small", style: { color: "#a4262c" } }, { children: "No products are available to select right now." })), onRetryAzureComponentOptions && (_jsx(Link, Object.assign({ disabled: !isUnlocked, onClick: onRetryAzureComponentOptions }, { children: "Retry loading products" })))] })) })))] })), _jsx(Stack, Object.assign({ horizontal: true, horizontalAlign: "start", style: { marginTop: "24px" } }, { children: _jsx(PrimaryButton, { text: "Review & Submit", onClick: handleReviewCreateCall, disabled: !isFormComplete(), styles: {
                                root: {
                                    backgroundColor: "#0078d4",
                                    borderColor: "#0078d4",
                                    minWidth: "180px",
                                    height: "50px", // Match tile height
                                },
                                rootHovered: {
                                    backgroundColor: "#106ebe",
                                    borderColor: "#106ebe",
                                },
                                rootPressed: {
                                    backgroundColor: "#005a9e",
                                    borderColor: "#005a9e",
                                },
                                rootDisabled: {
                                    backgroundColor: "#c8c6c4",
                                    borderColor: "#c8c6c4",
                                    color: "#a19f9d",
                                },
                            } }) }))] })))] })));
};
